import React, { useState } from "react";
import DOMPurify from "dompurify";
import API from "../services/api";

/* ==============================================
   ProfileModal: Display & Edit full client info
   ============================================== */
function ProfileModal({ isOpen, onClose, clientData, onSave }) {
  // If modal not open or no client data, hide it
  if (!isOpen || !clientData) return null;

  // We'll allow editing all these fields. Some may not exist in your DB,
  // so adjust as needed.
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(() => ({
    clientName: clientData.client_name || "",
    phone: clientData.phone || "",
    gender: clientData.gender || "",
    dob: clientData.dob || "",
    reasonForVisit: clientData.reason_for_visit || "",
    email: clientData.email || "",
    address: clientData.address || "",
    city: clientData.city || "",
    state: clientData.state || "",
    zipCode: clientData.zip_code || "",
    emergencyContactName: clientData.emergency_contact_name || "",
    emergencyContactPhone: clientData.emergency_contact_phone || ""
    // Add any additional fields your DB supports
  }));

  // Save changes to parent
  const handleSave = () => {
    onSave(form);
    setEditMode(false);
  };

  // A small helper for age from dob
  const calcAge = (dobString) => {
    if (!dobString) return "";
    const birthDate = new Date(dobString);
    if (isNaN(birthDate.getTime())) return "";
    const diffMs = Date.now() - birthDate.getTime();
    const ageDt = new Date(diffMs);
    return Math.abs(ageDt.getUTCFullYear() - 1970);
  };

  const age = calcAge(form.dob);

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>Client Profile</h2>
        <div style={{ textAlign: "right" }}>
          {!editMode && (
            <button
              onClick={() => setEditMode(true)}
              style={{ ...styles.button, backgroundColor: "var(--accent-color)" }}
            >
              Edit
            </button>
          )}
          <button onClick={onClose} style={styles.cancelBtn}>
            Close
          </button>
        </div>

        {/* Display or edit fields: */}
        <div style={styles.formGroup}>
          <label>Client Name:</label>
          {editMode ? (
            <input
              style={styles.input}
              value={form.clientName}
              onChange={(e) => setForm({ ...form, clientName: e.target.value })}
            />
          ) : (
            <div style={styles.readOnly}>{form.clientName}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Gender:</label>
          {editMode ? (
            <select
              style={styles.input}
              value={form.gender}
              onChange={(e) => setForm({ ...form, gender: e.target.value })}
            >
              <option value="">--Select--</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Nonbinary">Nonbinary</option>
              <option value="Other">Other</option>
            </select>
          ) : (
            <div style={styles.readOnly}>{form.gender}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Date of Birth:</label>
          {editMode ? (
            <input
              type="date"
              style={styles.input}
              value={form.dob}
              onChange={(e) => setForm({ ...form, dob: e.target.value })}
            />
          ) : (
            <div style={styles.readOnly}>{form.dob}</div>
          )}
          {!editMode && age && <div style={{ color: "var(--text-muted)" }}>Age: {age}</div>}
        </div>

        <div style={styles.formGroup}>
          <label>Reason for Visit:</label>
          {editMode ? (
            <textarea
              rows="2"
              style={styles.textArea}
              value={form.reasonForVisit}
              onChange={(e) =>
                setForm({ ...form, reasonForVisit: e.target.value })
              }
            />
          ) : (
            <div style={styles.readOnly}>{form.reasonForVisit}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Phone:</label>
          {editMode ? (
            <input
              style={styles.input}
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          ) : (
            <div style={styles.readOnly}>{form.phone}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Email:</label>
          {editMode ? (
            <input
              style={styles.input}
              value={form.email}
              onChange={(e) =>
                setForm({ ...form, email: e.target.value })
              }
            />
          ) : (
            <div style={styles.readOnly}>{form.email}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Address:</label>
          {editMode ? (
            <>
              <input
                style={{...styles.input, marginBottom: '0.5rem'}}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Street Address"
              />
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  style={{ flex: 2, padding: '0.5rem' }}
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="City"
                />
                <input
                  style={{ flex: 1, padding: '0.5rem' }}
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="State"
                />
                <input
                  style={{ flex: 1, padding: '0.5rem' }}
                  value={form.zipCode}
                  onChange={(e) => setForm({ ...form, zipCode: e.target.value })}
                  placeholder="Zip Code"
                />
              </div>
            </>
          ) : (
            <div style={styles.readOnly}>
              {form.address}
              {form.address && (form.city || form.state || form.zipCode) ? ', ' : ''}
              {form.city}
              {form.city && (form.state || form.zipCode) ? ', ' : ''}
              {form.state} {form.zipCode}
            </div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Emergency Contact Name:</label>
          {editMode ? (
            <input
              style={styles.input}
              value={form.emergencyContactName}
              onChange={(e) =>
                setForm({ ...form, emergencyContactName: e.target.value })
              }
            />
          ) : (
            <div style={styles.readOnly}>{form.emergencyContactName}</div>
          )}
        </div>

        <div style={styles.formGroup}>
          <label>Emergency Contact Phone:</label>
          {editMode ? (
            <input
              style={styles.input}
              value={form.emergencyContactPhone}
              onChange={(e) =>
                setForm({ ...form, emergencyContactPhone: e.target.value })
              }
            />
          ) : (
            <div style={styles.readOnly}>{form.emergencyContactPhone}</div>
          )}
        </div>

        {editMode && (
          <div style={styles.modalButtons}>
            <button onClick={handleSave} style={styles.saveBtn}>
              Save
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ==============================================
   Client Form Modal: Display client intake forms
   ============================================== */
function ClientFormModal({ isOpen, onClose, formData }) {
  if (!isOpen || !formData) return null;

  // Parse form data
  const recordData = formData.data_json || {};
  
  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Helper function to safely get signature data regardless of field names
  const getSignatureData = (form, type) => {
    if (!form) return '';
    
    // Check for different possible signature field names based on form structure
    let sigData = '';
    
    if (type === 'consent') {
      // Try all possible field names for consent signature
      sigData = form.consent_signature || 
                form.consentSignatureData || 
                (form.data_json && form.data_json.consentSignatureData) || 
                form.signature || 
                '';
    } else if (type === 'cancel') {
      // Try all possible field names for cancellation signature
      sigData = form.cancel_signature || 
                form.cancelSignatureData || 
                (form.data_json && form.data_json.cancelSignatureData) || 
                form.signature || 
                '';
    } else if (type === 'final') {
      // Try all possible field names for final signature
      sigData = form.final_signature || 
                form.finalSignatureData || 
                (form.data_json && form.data_json.finalSignatureData) || 
                form.signature || 
                '';
    }
    
    return sigData;
  };
  
  // Check if this is an intake form
  const isIntakeForm = recordData.clientName || recordData.consentDate || (recordData.basicInfo && recordData.basicInfo.name);
  
  // Map form data to display fields
  const clientName = recordData.clientName || 
                    (recordData.basicInfo ? recordData.basicInfo.name : '') || 
                    'Unknown';
  const clientAge = recordData.clientAge || '';
  const isMinor = recordData.isMinor || (clientAge && parseInt(clientAge, 10) < 18) || false;
  const guardianName = recordData.guardianName || '';
  const reasonForVisit = recordData.reasonForVisit || '';
  const formDate = formatDate(formData.created_at);
  const clientPhone = recordData.clientPhone || '';
  const clientEmail = recordData.clientEmail || '';
  const address = recordData.address || '';
  const city = recordData.city || '';
  const state = recordData.state || '';
  const zipCode = recordData.zipCode || '';
  const emergencyContactName = recordData.emergencyContactName || '';
  const emergencyContactPhone = recordData.emergencyContactPhone || '';
  
  // Extract signature data
  const consentSignatureData = getSignatureData(formData, 'consent');
  const cancelSignatureData = getSignatureData(formData, 'cancel');
  const finalSignatureData = getSignatureData(formData, 'final');
  
  // Dates and consent info
  const consentDate = recordData.consentDate ? formatDate(recordData.consentDate) : formatDate(formData.created_at);
  const cancelDate = recordData.cancelDate ? formatDate(recordData.cancelDate) : formatDate(formData.created_at);
  const initials = recordData.initials || '';
  
  // Handle download as PDF
  const downloadPdf = () => {
    // Create a new window for printing
    const newWin = window.open('', '_blank', 'width=800,height=900,scrollbars=yes');
    if (!newWin) {
      alert('Failed to open new window for PDF export. Please check your popup blocker settings.');
      return;
    }

    // Build HTML content based on form type
    let htmlContent = '';
    
    if (isIntakeForm) {
      // For intake forms, create a comprehensive view that matches the client intake page
      htmlContent = `
        <html>
        <head>
          <title>Client Intake Form</title>
          <style>
            body {
              font-family: 'Times New Roman', Times, serif;
              margin: 20px;
              padding: 20px;
              line-height: 1.5;
            }
            .page {
              border: 1px solid #000;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
              position: relative;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .footer {
              text-align: center;
              margin-top: 40px;
            }
            .section {
              margin-top: 40px;
              margin-bottom: 20px;
            }
            h1, h2, h3 {
              text-align: center;
            }
            .field {
              margin-bottom: 10px;
            }
            .field-label {
              font-weight: bold;
            }
            .signature-section {
              margin-top: 30px;
              border-top: 1px solid var(--border-color);
              padding-top: 20px;
            }
            .signature {
              width: 200px;
              height: auto;
            }
            .date-line {
              display: inline-block;
              margin-left: 40px;
            }
            .client-info {
              margin-top: 20px;
              border-top: 1px solid var(--border-color);
              padding-top: 20px;
            }
            .subsection {
              margin-top: 30px;
            }
            .indent {
              margin-left: 20px;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <!-- Header Image -->
            <div class="header">
              <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
            </div>
            
            <h2>CONSENT TO TREAT</h2>
            <p>
              I, ${isMinor ? guardianName : clientName}, 
              ${isMinor ? `give my permission and consent to Avenues Unlimited Counseling Center,
              Inc., to provide psychotherapeutic treatment to ${clientName}.` : 
              `give my permission and consent to Avenues Unlimited Counseling Center,
              Inc., to provide me psychotherapeutic treatment to myself.`}
            </p>
            <p>
              While Avenues Unlimited will endeavor to provide standard of care treatment, I fully understand that 
              because of factors beyond Avenues Unlimited's control, particular outcomes cannot be guaranteed. 
              Furthermore, I understand that I/he/she/we may experience emotional strains because of the counseling 
              or therapy, feel worse during the treatment and make life changes which could be difficult.
            </p>
            <p>
              I understand that Avenues Unlimited is not providing emergency services, and I have been informed of whom to call upon in an emergency
              or during such time as treatment from Avenues Unlimited is unavailable.
            </p>
            <p>
              I understand that regular attendance as recommended by Avenues Unlimited will facilitate maximum therapeutic benefits, but that I/we
              am/are free to discontinue treatment at any time. If I decide to do so I will notify Avenues Unlimited
              at least two weeks in advance so that effective planning for continuing care can be implemented.
            </p>

            <div>
              <p>Consent Signature:</p>
              <img src="${consentSignatureData}" class="signature" alt="Consent Signature">
              <span class="date-line">Date: ${consentDate}</span>
            </div>

            <div class="section">
              <h3>CANCELLATION POLICY</h3>
              <p>
                I understand that there is a 24 hour (or 1 working day) rescheduling policy. 
                A rescheduling charge of the full session fee will result if I do not give notice in this time frame.
              </p>
              
              <p>Cancellation Signature:</p>
              <img src="${cancelSignatureData}" class="signature" alt="Cancellation Signature">
              <span class="date-line">Date: ${cancelDate}</span>
            </div>

            <div class="section">
              <h3>LIMITS OF CONFIDENTIALITY</h3>
              <p>
                The contents of counseling, intake and/or assessment sessions are confidential. 
                Both verbal information and written records about a client cannot be shared with another party without 
                the written consent of the client or the client's legal guardian, except as required by law. 
                It is the policy of this center not to release any information about a client without a signed 
                release of information, or as otherwise required by law. Noted exceptions are as follows:
              </p>
              
              <div class="subsection">
                <h4>DUTY TO WARN AND PROTECT</h4>
                <p class="indent">
                  When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law 
                  to warn the intended victim and report identifying information to legal authorities. In cases in which 
                  the client discloses or implies a plan for suicide, Avenues Unlimited is required to notify legal 
                  authorities and make reasonable attempts to notify the family of the client.
                </p>
              </div>
              
              <div class="subsection">
                <h4>ABUSE OF CHILDREN AND VULNERABLE ADULTS</h4>
                <p class="indent">
                  If a client discloses or suggests that he or she is abusing a child (or vulnerable adult) or has recently 
                  abused a child (or vulnerable adult); or a child (or vulnerable adult) is in danger of abuse, 
                  Avenues Unlimited is required to report this information to the appropriate social service and/or legal authorities.
                </p>
              </div>
              
              <div class="subsection">
                <h4>PRENATAL EXPOSURE TO CONTROLLED SUBSTANCES</h4>
                <p class="indent">
                  Avenues Unlimited is required by law to report admitted prenatal exposure to controlled substances 
                  that are potentially harmful.
                </p>
              </div>
              
              <div class="subsection">
                <h4>IN THE EVENT OF A CLIENT'S DEATH</h4>
                <p class="indent">
                  In the event of a client's death, the spouse or parents of a deceased client have a right to access a client's treatment records.
                </p>
              </div>
              
              <div class="subsection">
                <h4>PROFESSIONAL MISCONDUCT</h4>
                <p class="indent">
                  Professional misconduct by a health care professional must be reported by other health care professionals. 
                  In cases in which a professional or legal disciplinary meeting is being held regarding the health care professional's actions, 
                  related records may be released in order to substantiate disciplinary concerns.
                </p>
              </div>
              
              <div class="subsection">
                <h4>COURT ORDERS</h4>
                <p class="indent">
                  Health care professionals are required to release records of clients when a court order has been entered.
                </p>
              </div>
              
              <div class="subsection">
                <h4>MINORS/GUARDIANSHIP</h4>
                <p class="indent">
                  Parents or legal guardians of non-emancipated minor clients have the right to access the client's records.
                </p>
              </div>
              
              <p>
                I have been informed of the limits of confidentiality. Initials: <strong>${initials}</strong>
              </p>
            </div>
            
            <div class="section">
              <h3>OTHER PROVISIONS</h3>
              <p>
                Payment for services is due and payable at the time services are rendered by Avenues Unlimited. 
                In the event that you fail to pay for any such services when rendered, Avenues Unlimited reserves the right 
                to turn delinquent accounts over to collection agencies and/or attorneys without prior notice. 
                Unpaid account balances accrue interest at the rate of 1.5% per month until paid. Further, in the event 
                Avenues Unlimited incurs attorney's fees and/or court costs to collect delinquent account balances, 
                client agrees to pay or reimburse Avenues Unlimited for the same. Avenues Unlimited may report delinquent 
                account balances to credit reporting agencies, and the client's credit report may state the amount owed, 
                time frame and the name of the clinic.
              </p>
              
              <p>
                Avenues Unlimited may furnish insurance companies and other third-party payers information regarding 
                services rendered by Avenues Unlimited to clients. Information which may be furnished by Avenues Unlimited 
                to these parties includes a description of the type of services, date/times of services, diagnosis, 
                treatment plan, description of impairment, progress of therapy, case notes and summaries. 
                Please note that Avenues Unlimited does not accept insurance payments unless otherwise agreed to in 
                writing between Avenues Unlimited and client.
              </p>
              
              <p>
                When couples, groups or families are receiving services, separate files are kept for individuals for 
                information disclosed that is of a confidential nature. This information includes (a) testing results, 
                (b) information given to the mental health professional not in the presence of the other person(s) 
                utilizing services, (c) information received from other sources about the client, (d) diagnosis, 
                (e) treatment plan, (f) individual reports/summaries and (h) information that has been requested to be separate. 
                The material disclosed in conjoint family or couples sessions in which each part discloses such information 
                in each other's presences, is kept in each file in the form of case notes.
              </p>
              
              <p>
                In the event Avenues Unlimited must telephone or e-mail the client for purposes such as appointments, 
                cancellations or reminders or to give or receive other information, efforts are made to preserve confidentiality.
                
                <br><br>
                Phone Contact: ${clientPhone || 'Not provided'}
                <br>
                Email Contact: ${clientEmail || 'Not provided'}
                <br><br>
                Emergency Contact: ${emergencyContactName || 'Not provided'}, ${emergencyContactPhone || 'Not provided'}
              </p>
            </div>
            
            <div class="section">
              <h3>I AGREE TO THESE PROVISIONS</h3>
              <p>
                By signing below, I acknowledge that I have read, understood, and agree to all the terms 
                and conditions outlined in this consent form. I have been given the opportunity to ask questions
                about the content of this form, and I understand my rights and responsibilities as a client at
                Avenues Unlimited Counseling Center.
              </p>
              
              <p>Signature:</p>
              <img src="${finalSignatureData}" class="signature" alt="Final Acknowledgment Signature">
              <span class="date-line">Date: ${consentDate}</span>
            </div>

            <div class="client-info">
              <h3>Client Information</h3>
              <p><strong>Client Name:</strong> ${clientName}</p>
              <p><strong>Client Age:</strong> ${clientAge}</p>
              ${isMinor ? `<p><strong>Guardian Name:</strong> ${guardianName}</p>` : ''}
              ${reasonForVisit ? `<p><strong>Reason for Visit:</strong> ${reasonForVisit}</p>` : ''}
              ${clientPhone ? `<p><strong>Phone:</strong> ${clientPhone}</p>` : ''}
              ${clientEmail ? `<p><strong>Email:</strong> ${clientEmail}</p>` : ''}
              ${address || city || state || zipCode ? 
                `<p><strong>Address:</strong> ${address}${address && (city || state || zipCode) ? ', ' : ''}
                ${city}${city && (state || zipCode) ? ', ' : ''}
                ${state} ${zipCode}</p>` : ''}
              <p><strong>Emergency Contact:</strong> ${emergencyContactName || 'Not provided'}, ${emergencyContactPhone || 'Not provided'}</p>
            </div>

            <!-- Footer Image -->
            <div class="footer">
              <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
        </html>
      `;
    } else if (recordData.storedFields && Array.isArray(recordData.storedFields)) {
      // For Acuity fields
      let fieldsHtml = '';
      recordData.storedFields.forEach(item => {
        fieldsHtml += `
          <div class="field">
            <span class="field-label">${item.label}:</span> ${item.value}
          </div>
        `;
      });
      
      htmlContent = `
        <html>
        <head>
          <title>Client Form Data</title>
          <style>
            body {
              font-family: 'Times New Roman', Times, serif;
              margin: 20px;
              padding: 20px;
              line-height: 1.5;
            }
            .page {
              border: 1px solid #000;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            .field {
              margin-bottom: 10px;
            }
            .field-label {
              font-weight: bold;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
              <h1>Client Form Data</h1>
              <p><strong>Date Submitted:</strong> ${formDate}</p>
            </div>
            
            <div>
              ${fieldsHtml}
            </div>
            
            <div class="footer">
              <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
        </html>
      `;
    } else {
      // Generic form data display
      htmlContent = `
        <html>
        <head>
          <title>Client Form Data</title>
          <style>
            body {
              font-family: 'Times New Roman', Times, serif;
              margin: 20px;
              padding: 20px;
              line-height: 1.5;
            }
            .page {
              border: 1px solid #000;
              padding: 40px;
              max-width: 800px;
              margin: 0 auto;
            }
            .header {
              text-align: center;
              margin-bottom: 20px;
            }
            pre {
              white-space: pre-wrap;
              overflow-wrap: break-word;
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <img src="https://www.avenuesportal.com/letterhead.png" alt="Avenues Letterhead" style="max-width: 100%;">
              <h1>Client Form Data</h1>
              <p><strong>Date Submitted:</strong> ${formDate}</p>
            </div>
            
            <pre>${JSON.stringify(recordData, null, 2)}</pre>
            
            <div class="footer">
              <img src="https://www.avenuesportal.com/footer.png" alt="Avenues Footer" style="max-width: 100%;">
            </div>
          </div>
          
          <script>
            window.onload = function() {
              window.focus();
              window.print();
            };
          </script>
        </body>
        </html>
      `;
    }
    
    newWin.document.write(htmlContent);
    newWin.document.close();
  };

  // Helper to determine form type for display
  const determineFormType = () => {
    if (recordData.clientName || recordData.consentDate) {
      return "Intake Form";
    }
    
    if (recordData.storedFields && Array.isArray(recordData.storedFields)) {
      return "Acuity Intake";
    }
    
    if (recordData.basicInfo) {
      return "Manual Intake";
    }
    
    return "Client Form";
  };
  
  // Render form data in HTML format
  const renderFormDataAsHtml = () => {
    let html = '';
    
    if (determineFormType() === "Intake Form") {
      html += '<h3>Client Information</h3>';
      html += `<div class="field"><span class="field-label">Name:</span> <span>${clientName}</span></div>`;
      
      if (clientAge) {
        html += `<div class="field"><span class="field-label">Age:</span> <span>${clientAge}</span></div>`;
      }
      
      if (isMinor && guardianName) {
        html += `<div class="field"><span class="field-label">Guardian Name:</span> <span>${guardianName}</span></div>`;
      }
      
      if (reasonForVisit) {
        html += `<div class="field"><span class="field-label">Reason for Visit:</span> <span>${reasonForVisit}</span></div>`;
      }
      
      if (clientPhone || clientEmail) {
        html += '<h3>Contact Information</h3>';
        if (clientPhone) {
          html += `<div class="field"><span class="field-label">Phone:</span> <span>${clientPhone}</span></div>`;
        }
        if (clientEmail) {
          html += `<div class="field"><span class="field-label">Email:</span> <span>${clientEmail}</span></div>`;
        }
      }
      
      if (address || city || state || zipCode) {
        html += '<h3>Address</h3>';
        html += `<div class="field"><span class="field-label">Address:</span> <span>${address || ''}</span></div>`;
        
        let cityStateZip = '';
        if (city) cityStateZip += city;
        if (city && (state || zipCode)) cityStateZip += ', ';
        if (state) cityStateZip += state;
        if (state && zipCode) cityStateZip += ' ';
        if (zipCode) cityStateZip += zipCode;
        
        if (cityStateZip) {
          html += `<div class="field"><span class="field-label">City, State, Zip:</span> <span>${cityStateZip}</span></div>`;
        }
      }
      
      if (emergencyContactName || emergencyContactPhone) {
        html += '<h3>Emergency Contact</h3>';
        if (emergencyContactName) {
          html += `<div class="field"><span class="field-label">Name:</span> <span>${emergencyContactName}</span></div>`;
        }
        if (emergencyContactPhone) {
          html += `<div class="field"><span class="field-label">Phone:</span> <span>${emergencyContactPhone}</span></div>`;
        }
      }
      
      // Show if signatures are present
      html += '<h3>Signatures</h3>';
      
      // Check for signature data 
      const hasConsentSig = consentSignatureData ? 'Present' : 'Missing';
      const hasCancelSig = cancelSignatureData ? 'Present' : 'Missing';
      const hasFinalSig = finalSignatureData ? 'Present' : 'Missing';
      
      html += `<div class="field"><span class="field-label">Consent Signature:</span> <span>${hasConsentSig}</span></div>`;
      html += `<div class="field"><span class="field-label">Cancellation Signature:</span> <span>${hasCancelSig}</span></div>`;
      html += `<div class="field"><span class="field-label">Final Signature:</span> <span>${hasFinalSig}</span></div>`;
      
      if (initials) {
        html += `<div class="field"><span class="field-label">Confidentiality Initials:</span> <span>${initials}</span></div>`;
      }
      
      return html;
    } else if (determineFormType() === "Acuity Intake") {
      html += '<h3>Acuity Form Fields</h3>';
      recordData.storedFields.forEach((item, idx) => {
        html += `<div class="field"><span class="field-label">${item.label}:</span> <span>${item.value}</span></div>`;
      });
      
      return html;
    } else if (determineFormType() === "Manual Intake") {
      html += '<h3>Basic Info</h3>';
      html += `<div class="field"><span class="field-label">Name:</span> <span>${recordData.basicInfo?.name || ''}</span></div>`;
      html += `<div class="field"><span class="field-label">Phone:</span> <span>${recordData.basicInfo?.phone || ''}</span></div>`;
      html += `<div class="field"><span class="field-label">Email:</span> <span>${recordData.basicInfo?.email || ''}</span></div>`;
      
      if (recordData.priceAndLocation) {
        html += '<h3>Price & Location</h3>';
        html += `<div class="field"><span class="field-label">Price:</span> <span>${recordData.priceAndLocation.price || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">Location:</span> <span>${recordData.priceAndLocation.location || ''}</span></div>`;
      }
      
      if (recordData.contactPersonal) {
        html += '<h3>Contact / Personal</h3>';
        html += `<div class="field"><span class="field-label">Guardian Name:</span> <span>${recordData.contactPersonal.guardianName || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">Gender:</span> <span>${recordData.contactPersonal.gender || ''}</span></div>`;
        html += `<div class="field"><span class="field-label">DOB:</span> <span>${recordData.contactPersonal.dob || ''}</span></div>`;
      }
      
      return html;
    }
    
    // Default: just stringify the JSON
    return `<pre>${JSON.stringify(recordData, null, 2)}</pre>`;
  };

  // For the form preview inside the modal
  const renderFormPreview = () => {
    if (isIntakeForm && (consentSignatureData || cancelSignatureData || finalSignatureData)) {
      return (
        <iframe
          sandbox=""
          srcDoc={`
            <html>
            <head>
              <style>
                body {
                  font-family: 'Times New Roman', Times, serif;
                  margin: 20px;
                  padding: 10px;
                  line-height: 1.5;
                }
                .page {
                  border: 1px solid var(--border-color);
                  padding: 20px;
                  position: relative;
                }
                .header {
                  text-align: center;
                  margin-bottom: 10px;
                }
                .signature {
                  width: 150px;
                  height: auto;
                }
                h2, h3 {
                  text-align: center;
                  margin-bottom: 10px;
                  font-size: 16px;
                }
                p {
                  font-size: 14px;
                  margin: 8px 0;
                }
                .section {
                  margin-top: 20px;
                }
                .date-line {
                  display: inline-block;
                  margin-left: 20px;
                  font-size: 14px;
                }
                .subsection {
                  margin-top: 15px;
                }
                .indent {
                  margin-left: 15px;
                }
              </style>
            </head>
            <body>
              <div class="page">
                <h2>CONSENT TO TREAT</h2>
                <p>
                  I, ${isMinor ? guardianName : clientName}, 
                  ${isMinor ? `give my permission and consent to Avenues Unlimited Counseling Center,
                  Inc., to provide psychotherapeutic treatment to ${clientName}.` : 
                  `give my permission and consent to Avenues Unlimited Counseling Center,
                  Inc., to provide me psychotherapeutic treatment to myself.`}
                </p>

                <div>
                  <p>Consent Signature:</p>
                  <img src="${consentSignatureData}" class="signature" alt="Consent Signature">
                  <span class="date-line">Date: ${consentDate}</span>
                </div>

                <div class="section">
                  <h3>CANCELLATION POLICY</h3>
                  <p>
                    I understand that there is a 24 hour (or 1 working day) rescheduling policy. 
                    A rescheduling charge of the full session fee will result if I do not give notice in this time frame.
                  </p>
                  
                  <p>Cancellation Signature:</p>
                  <img src="${cancelSignatureData}" class="signature" alt="Cancellation Signature">
                  <span class="date-line">Date: ${cancelDate}</span>
                </div>

                <div class="section">
                  <h3>LIMITS OF CONFIDENTIALITY</h3>
                  <p>
                    The contents of counseling, intake and/or assessment sessions are confidential. 
                    Both verbal information and written records about a client cannot be shared with another party without 
                    the written consent of the client or the client's legal guardian, except as required by law.
                  </p>
                  
                  <div class="subsection">
                    <h4>DUTY TO WARN AND PROTECT</h4>
                    <p class="indent">
                      When a client discloses intentions or a plan to harm another person, Avenues Unlimited is required by law 
                      to warn the intended victim and report identifying information to legal authorities.
                    </p>
                  </div>
                  
                  <p>Initials: <strong>${initials}</strong></p>
                </div>

                <div class="section">
                  <h3>OTHER PROVISIONS</h3>
                  <p>
                    Payment for services is due and payable at the time services are rendered by Avenues Unlimited. 
                    Contact info: ${clientPhone || 'Not provided'} / ${clientEmail || 'Not provided'}
                  </p>
                </div>

                <div class="section">
                  <h3>I AGREE TO THESE PROVISIONS</h3>
                  <p>
                    By signing below, I acknowledge that I have read, understood, and agree to all the terms 
                    and conditions outlined in this consent form.
                  </p>
                  
                  <p>Signature:</p>
                  <img src="${finalSignatureData}" class="signature" alt="Final Acknowledgment Signature">
                  <span class="date-line">Date: ${consentDate}</span>
                </div>
                
                <div style="margin-top: 15px;">
                  <h4>Client Information:</h4>
                  <p><strong>Client Name:</strong> ${clientName}</p>
                  <p><strong>Age:</strong> ${clientAge}</p>
                  ${isMinor ? `<p><strong>Guardian:</strong> ${guardianName}</p>` : ''}
                  ${reasonForVisit ? `<p><strong>Reason for Visit:</strong> ${reasonForVisit}</p>` : ''}
                  ${address || city || state || zipCode ? 
                    `<p><strong>Address:</strong> ${address} ${city}, ${state} ${zipCode}</p>` : ''}
                  <p><strong>Emergency Contact:</strong> ${emergencyContactName || 'Not provided'}, ${emergencyContactPhone || 'Not provided'}</p>
                </div>
              </div>
            </body>
            </html>
          `}
          style={{width: '100%', height: '500px', border: '1px solid var(--border-color)'}}
          title="Form Preview"
        />
      );
    } else {
      return (
        <div 
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(renderFormDataAsHtml()) }}
          style={{
            maxHeight: '500px',
            overflowY: 'auto',
            padding: '1rem',
            border: '1px solid var(--border-color)',
            borderRadius: '4px',
            background: '#fcfcfc'
          }}
        />
      );
    }
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={{
        ...styles.modalContent, 
        width: '90%', 
        maxWidth: '800px', 
        maxHeight: '90vh', 
        overflowY: 'auto'
      }}>
        <h2 style={styles.modalHeader}>{determineFormType()} - {formDate}</h2>
        <div style={{ textAlign: "right" }}>
          <button onClick={onClose} style={styles.cancelBtn}>
            Close
          </button>
        </div>
        
        {renderFormPreview()}
        
        <div style={{ marginTop: '1rem', textAlign: 'right' }}>
          <button 
            onClick={downloadPdf}
            style={{
              ...styles.button,
              backgroundColor: 'var(--success-color)'
            }}
          >
            Download PDF
          </button>
        </div>
      </div>
    </div>
  );
}

/* ==============================================
   Generate Intake Link Modal (with 7-day auto-expiration)
   ============================================== */
function GenerateIntakeLinkModal({ isOpen, onClose, clientId, clientName }) {
  if (!isOpen) return null;
  
  const [generatedLink, setGeneratedLink] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  // Generate the intake link
  const handleGenerateLink = async () => {
    if (!clientId) {
      setErrorMsg('Client information is missing. Please try again.');
      return;
    }
    
    setLoading(true);
    try {
      // Calculate date 7 days from now for auto-expiration
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + 7);
      
      const body = {
        clientId: clientId,
        expiresAt: expirationDate.toISOString()
      };

      const res = await API.post('/forms/intake/generate', body);

      if (res.data.success) {
        setGeneratedLink(res.data.link);
        setErrorMsg('');
      } else {
        setErrorMsg(res.data.error || 'Error generating link.');
      }
    } catch (err) {
      console.error('Error generating link:', err);
      setErrorMsg('Server error generating link.');
    } finally {
      setLoading(false);
    }
  };
  
  // Copy link to clipboard
  const copyToClipboard = () => {
    if (!generatedLink) return;
    
    navigator.clipboard.writeText(generatedLink)
      .then(() => {
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 3000);
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
      });
  };

  return (
    <div style={styles.modalBackdrop}>
      <div style={styles.modalContent}>
        <h2 style={styles.modalHeader}>Generate Intake Link</h2>
        
        <div style={{ marginBottom: '1rem' }}>
          <p>Generate an intake form link for <strong>{clientName}</strong> to complete.</p>
          <p>The link will automatically expire after 7 days.</p>
        </div>
        
        {errorMsg && (
          <p style={{ color: 'red', marginTop: '0.5rem' }}>{errorMsg}</p>
        )}
        
        {!generatedLink ? (
          <div style={styles.modalButtons}>
            <button 
              onClick={handleGenerateLink} 
              style={styles.saveBtn}
              disabled={loading}
            >
              {loading ? 'Generating...' : 'Generate Link'}
            </button>
            <button onClick={onClose} style={styles.cancelBtn}>
              Cancel
            </button>
          </div>
        ) : (
          <div>
            <div style={{
              padding: '1rem',
              backgroundColor: '#f7f7f7',
              border: '1px solid var(--border-color)',
              borderRadius: '4px',
              marginTop: '1rem',
              wordBreak: 'break-all'
            }}>
              <p style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>Client Intake Link:</p>
              <a 
                href={generatedLink} 
                target="_blank" 
                rel="noreferrer"
                style={{ color: '#0066cc' }}
              >
                {generatedLink}
              </a>
              <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '0.5rem' }}>
                This link will expire in 7 days on {new Date(new Date().setDate(new Date().getDate() + 7)).toLocaleDateString()}.
              </p>
            </div>
            
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
              <button 
                onClick={copyToClipboard}
                style={{
                  ...styles.button,
                  backgroundColor: linkCopied ? 'var(--success-color)' : '#0066cc'
                }}
              >
                {linkCopied ? 'Copied!' : 'Copy Link'}
              </button>
              
              <button onClick={onClose} style={styles.button}>
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Styles imported from the main component
const styles = {
  modalBackdrop: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 9999,
    padding: "1rem"
  },
  modalContent: {
    backgroundColor: "#fff",
    padding: "1rem",
    borderRadius: "6px",
    maxHeight: "90vh",
    overflowY: "auto",
    width: "500px",
    maxWidth: "94vw",
    boxSizing: "border-box",
    position: "relative"
  },
  modalHeader: {
    fontSize: "1.25rem",
    margin: 0,
    marginBottom: "0.5rem"
  },
  formGroup: {
    marginBottom: "0.75rem"
  },
  input: {
    width: "100%",
    padding: "0.4rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    boxSizing: "border-box"
  },
  textArea: {
    width: "100%",
    padding: "0.4rem",
    border: "1px solid var(--border-color)",
    borderRadius: "4px",
    boxSizing: "border-box"
  },
  modalButtons: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "0.5rem",
    marginTop: "1rem"
  },
  saveBtn: {
    backgroundColor: "var(--success-color)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  cancelBtn: {
    backgroundColor: "var(--text-strong)",
    border: "none",
    padding: "0.5rem 1rem",
    color: "#fff",
    borderRadius: "4px",
    cursor: "pointer"
  },
  button: {
    backgroundColor: "var(--primary-color)",
    border: "none",
    color: "#fff",
    padding: "0.4rem 0.75rem",
    borderRadius: "4px",
    cursor: "pointer"
  },
  readOnly: {
    backgroundColor: "var(--surface-muted)",
    padding: "0.4rem",
    borderRadius: "4px"
  }
};

export { ProfileModal, ClientFormModal, GenerateIntakeLinkModal };